import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authHeaders } from './listPageStyles';

// Epic D — Akış (node-graph) editörü.
// GET/PUT /api/surveys/{id}/flow ile çalışır. Düğümler: Start (yeşil) / End (kırmızı) /
// Question. Liste tipi sorularda her seçenek ayrı çıkış; diğer tiplerde tek çıkış.

const NODE_W = 210;
const HEADER_H = 38;
const ROW_H = 28;
const PORT_R = 6;

const CHOICE_TYPES = ['List', 'SingleChoice', 'MultiChoice'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

// Bir sorunun çıkışları: liste tipinde her seçenek, diğerlerinde tek çıkış.
function outputsOf(q) {
  if (!q) return [{ optionId: null, label: 'Sonraki Soru' }];
  const type = pick(q, 'questionType', 'QuestionType');
  if (CHOICE_TYPES.includes(type)) {
    let opts = pick(q, 'options', 'Options');
    if (!Array.isArray(opts) || opts.length === 0) {
      try { opts = (JSON.parse(pick(q, 'optionsJson', 'OptionsJson') || '[]')).map((t, i) => ({ id: `idx${i}`, text: t })); }
      catch { opts = []; }
    }
    if (opts.length === 0) return [{ optionId: null, label: 'Sonraki Soru' }];
    return opts.map((o) => ({ optionId: pick(o, 'id', 'Id'), label: pick(o, 'text', 'Text', 'OptionText') || '—' }));
  }
  return [{ optionId: null, label: 'Sonraki Soru' }];
}

export default function SurveyFlowEditor({ surveyId, showToast, readOnly = false }) {
  const [questions, setQuestions] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedQid, setSelectedQid] = useState(null); // seçili düğüm: 'start' | 'end' | questionId
  const [interaction, setInteraction] = useState(null);  // {type:'node'|'conn', ...}
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const [savedTick, setSavedTick] = useState(0); // "Kaydedildi" göstergesi
  const canvasRef = useRef(null);

  const API = `/api/surveys/${surveyId}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, fRes] = await Promise.all([
        fetch(`${API}/questions`, { headers: authHeaders() }),
        fetch(`${API}/flow`, { headers: authHeaders() }),
      ]);
      const qBody = unwrap(await qRes.json());
      const fBody = unwrap(await fRes.json());
      const qs = (pick(qBody, 'items', 'Items') || []).filter((q) => pick(q, 'questionType', 'QuestionType') !== 'SectionHeader');
      setQuestions(qs);
      setNodes(pick(fBody, 'nodes', 'Nodes') || []);
      setConnections((pick(fBody, 'connections', 'Connections') || []).map((c) => ({ ...c, _k: pick(c, 'id', 'Id') || Math.random() })));
      // Backend varsayılan (kaydedilmemiş) akış döndürdüyse "kaydedilmedi" işaretle ki
      // kullanıcı Kaydet'e basıp kalıcılaştırsın; aksi halde temiz/kaydedilmiş.
      setDirty(!!pick(fBody, 'defaulted', 'Defaulted'));
    } catch (err) {
      showToast?.('error', err.message || 'Akış yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [API, showToast]);

  useEffect(() => { load(); }, [load]);

  const questionById = useMemo(() => {
    const m = {};
    questions.forEach((q) => { m[pick(q, 'id', 'Id')] = q; });
    return m;
  }, [questions]);

  const startNode = nodes.find((n) => pick(n, 'nodeKind', 'NodeKind') === 'Start');
  const endNode = nodes.find((n) => pick(n, 'nodeKind', 'NodeKind') === 'End');
  const qNode = (qid) => nodes.find((n) => pick(n, 'nodeKind', 'NodeKind') === 'Question' && pick(n, 'questionId', 'QuestionId') === qid);

  // Çıkış noktası ekran koordinatı
  const outPoint = (node, outIdx) => ({
    x: pick(node, 'x', 'X') + NODE_W,
    y: pick(node, 'y', 'Y') + HEADER_H + ROW_H * outIdx + ROW_H / 2,
  });
  const inPoint = (node) => ({ x: pick(node, 'x', 'X'), y: pick(node, 'y', 'Y') + HEADER_H / 2 });

  // Bir bağlantının kaynak/hedef ekran noktaları
  const connPoints = (c) => {
    const srcQid = pick(c, 'sourceQuestionId', 'SourceQuestionId');
    const optId = pick(c, 'sourceOptionId', 'SourceOptionId');
    const tgtQid = pick(c, 'targetQuestionId', 'TargetQuestionId');
    const src = srcQid ? qNode(srcQid) : startNode;
    const tgt = tgtQid ? qNode(tgtQid) : endNode;
    if (!src || !tgt) return null;
    let outIdx = 0;
    if (srcQid) {
      const outs = outputsOf(questionById[srcQid]);
      outIdx = Math.max(0, outs.findIndex((o) => o.optionId === optId));
    }
    return { from: outPoint(src, outIdx), to: inPoint(tgt) };
  };

  const bezier = (a, b) => `M ${a.x} ${a.y} C ${a.x + 60} ${a.y}, ${b.x - 60} ${b.y}, ${b.x} ${b.y}`;

  // ── Etkileşim: düğüm taşıma + bağlantı çizme ──
  const localXY = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left + canvasRef.current.scrollLeft, y: e.clientY - r.top + canvasRef.current.scrollTop };
  };

  const startNodeDrag = (e, node) => {
    e.stopPropagation();
    const p = localXY(e);
    setInteraction({ type: 'node', nodeId: pick(node, 'id', 'Id'), dx: p.x - pick(node, 'x', 'X'), dy: p.y - pick(node, 'y', 'Y') });
  };

  const startConn = (e, srcQid, optId) => {
    e.stopPropagation();
    const p = localXY(e);
    setInteraction({ type: 'conn', sourceQuestionId: srcQid, sourceOptionId: optId });
    setPointer(p);
  };

  const onCanvasMove = (e) => {
    if (!interaction) return;
    const p = localXY(e);
    if (interaction.type === 'node') {
      setNodes((ns) => ns.map((n) => (pick(n, 'id', 'Id') === interaction.nodeId
        ? { ...n, x: Math.max(0, p.x - interaction.dx), X: Math.max(0, p.x - interaction.dx), y: Math.max(0, p.y - interaction.dy), Y: Math.max(0, p.y - interaction.dy) }
        : n)));
      setDirty(true);
    } else {
      setPointer(p);
    }
  };

  const onCanvasUp = () => {
    if (interaction?.type === 'conn') setInteraction(null); // hedef düğüm üstünde bırakılmadıysa iptal
    else setInteraction(null);
  };

  // Bağlantı hedefi: bir düğüm üzerinde mouse-up
  const dropOnTarget = (e, targetQid /* null = End */) => {
    if (interaction?.type !== 'conn') return;
    e.stopPropagation();
    setConnectionTarget(interaction.sourceQuestionId, interaction.sourceOptionId, targetQid);
    setInteraction(null);
  };

  // Bir çıkışın hedefini ata/güncelle/sil (dropdown ve sürükle ortak)
  const setConnectionTarget = (srcQid, optId, targetQid) => {
    setConnections((cs) => {
      const rest = cs.filter((c) => !(
        (pick(c, 'sourceQuestionId', 'SourceQuestionId') ?? null) === (srcQid ?? null) &&
        (pick(c, 'sourceOptionId', 'SourceOptionId') ?? null) === (optId ?? null)
      ));
      if (targetQid === undefined) return rest; // sil
      return [...rest, {
        sourceQuestionId: srcQid ?? null,
        sourceOptionId: optId ?? null,
        targetQuestionId: targetQid ?? null,
        priority: 0,
        _k: Math.random(),
      }];
    });
    setDirty(true);
  };

  const connFor = (srcQid, optId) => connections.find((c) => (
    (pick(c, 'sourceQuestionId', 'SourceQuestionId') ?? null) === (srcQid ?? null) &&
    (pick(c, 'sourceOptionId', 'SourceOptionId') ?? null) === (optId ?? null)
  ));

  const setPriority = (c, val) => {
    setConnections((cs) => cs.map((x) => (x._k === c._k ? { ...x, priority: Number(val) || 0 } : x)));
    setDirty(true);
  };

  // ── Doğrulama: erişilemez + döngü (D12) ──
  const validation = useMemo(() => {
    const adj = {}; // qid|'start' -> [targetQid|'end']
    const key = (qid) => qid ?? 'start';
    connections.forEach((c) => {
      const s = key(pick(c, 'sourceQuestionId', 'SourceQuestionId'));
      const t = pick(c, 'targetQuestionId', 'TargetQuestionId') ?? 'end';
      (adj[s] = adj[s] || []).push(t);
    });
    // erişilebilirlik (BFS from start)
    const reach = new Set(['start']);
    const queue = ['start'];
    while (queue.length) {
      const cur = queue.shift();
      (adj[cur] || []).forEach((t) => { if (!reach.has(t)) { reach.add(t); queue.push(t); } });
    }
    const unreachable = questions.filter((q) => !reach.has(pick(q, 'id', 'Id'))).map((q) => pick(q, 'questionText', 'QuestionText'));
    // döngü (DFS)
    let hasCycle = false;
    const color = {};
    const dfs = (u) => {
      color[u] = 1;
      (adj[u] || []).forEach((v) => {
        if (v === 'end') return;
        if (color[v] === 1) hasCycle = true;
        else if (!color[v]) dfs(v);
      });
      color[u] = 2;
    };
    dfs('start');
    questions.forEach((q) => { const id = pick(q, 'id', 'Id'); if (!color[id]) dfs(id); });
    return { unreachable, hasCycle };
  }, [connections, questions]);

  // Verilen düğüm+bağlantıları kaydeder. Autosave ve manuel kayıt ortak kullanır.
  const doSave = useCallback(async (nodesArg, connsArg, { silent = false } = {}) => {
    if (readOnly) return;
    setSaving(true);
    try {
      const payload = {
        nodes: nodesArg.map((n) => ({
          nodeKind: pick(n, 'nodeKind', 'NodeKind'),
          questionId: pick(n, 'questionId', 'QuestionId') ?? null,
          x: pick(n, 'x', 'X'), y: pick(n, 'y', 'Y'),
        })),
        connections: connsArg.map((c) => ({
          sourceQuestionId: pick(c, 'sourceQuestionId', 'SourceQuestionId') ?? null,
          sourceOptionId: pick(c, 'sourceOptionId', 'SourceOptionId') ?? null,
          targetQuestionId: pick(c, 'targetQuestionId', 'TargetQuestionId') ?? null,
          priority: pick(c, 'priority', 'Priority') ?? 0,
        })),
      };
      const res = await fetch(`${API}/flow`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Akış kaydedilemedi (${res.status})`);
      // Backend Id'leriyle bağlantıları tazele.
      const f = unwrap(body);
      setConnections((pick(f, 'connections', 'Connections') || []).map((c) => ({ ...c, _k: pick(c, 'id', 'Id') || Math.random() })));
      setDirty(false);
      setSavedTick((t) => t + 1);
      if (!silent) showToast?.('success', 'Akış kaydedildi.');
    } catch (err) {
      showToast?.('error', err.message || 'Akış kaydedilemedi');
    } finally {
      setSaving(false);
    }
  }, [API, showToast, readOnly]);

  const save = () => doSave(nodes, connections);

  // Kanvas içerik boyutu (düğüm yayılımına göre)
  const extent = nodes.reduce((m, n) => ({
    w: Math.max(m.w, pick(n, 'x', 'X') + NODE_W + 80),
    h: Math.max(m.h, pick(n, 'y', 'Y') + HEADER_H + ROW_H * 6 + 80),
  }), { w: 900, h: 520 });

  if (loading) return <div className="dps-empty"><span>Akış yükleniyor…</span></div>;
  if (questions.length === 0) {
    return <div className="dps-empty"><p>Akış için önce soru ekleyin</p><span>“Sorular” sekmesinden veri toplayan en az bir soru ekleyin.</span></div>;
  }

  const targetOptions = (srcQid) => [
    { value: '__none__', label: '— bağlı değil' },
    { value: '__end__', label: 'Anketi Bitir' },
    ...questions.filter((q) => pick(q, 'id', 'Id') !== srcQid).map((q) => ({ value: pick(q, 'id', 'Id'), label: pick(q, 'questionText', 'QuestionText') || '—' })),
  ];

  const renderNode = (node) => {
    const kind = pick(node, 'nodeKind', 'NodeKind');
    const x = pick(node, 'x', 'X'); const y = pick(node, 'y', 'Y');
    const isSel = (kind === 'Start' && selectedQid === 'start') || (kind === 'End' && selectedQid === 'end') ||
      (kind === 'Question' && selectedQid === pick(node, 'questionId', 'QuestionId'));
    const headerBg = kind === 'Start' ? '#1AA37A' : kind === 'End' ? '#dc2626' : '#4F46E5';
    const q = kind === 'Question' ? questionById[pick(node, 'questionId', 'QuestionId')] : null;
    const outs = kind === 'End' ? [] : (kind === 'Start' ? [{ optionId: null, label: 'İlk Soru' }] : outputsOf(q));
    const selKey = kind === 'Start' ? 'start' : kind === 'End' ? 'end' : pick(node, 'questionId', 'QuestionId');
    return (
      <div
        key={pick(node, 'id', 'Id')}
        onMouseDown={(e) => { startNodeDrag(e, node); setSelectedQid(selKey); }}
        onMouseUp={(e) => { if (kind === 'End') dropOnTarget(e, null); else if (kind === 'Question') dropOnTarget(e, pick(node, 'questionId', 'QuestionId')); }}
        style={{
          position: 'absolute', left: x, top: y, width: NODE_W,
          background: '#fff', border: '2px solid ' + (isSel ? '#4F46E5' : '#e5e7eb'),
          borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', cursor: 'grab', userSelect: 'none', zIndex: 2,
        }}
      >
        <div style={{ height: HEADER_H, background: headerBg, color: '#fff', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', padding: '0 10px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {kind === 'Start' ? 'Başlangıç' : kind === 'End' ? 'Bitiş' : (pick(q, 'questionText', 'QuestionText') || 'Soru')}
        </div>
        {/* giriş noktası (sol) */}
        {kind !== 'Start' && (
          <div style={{ position: 'absolute', left: -PORT_R, top: HEADER_H / 2 - PORT_R, width: PORT_R * 2, height: PORT_R * 2, borderRadius: '50%', background: '#94a3b8', border: '2px solid #fff' }} />
        )}
        {/* çıkışlar */}
        {outs.map((o, i) => (
          <div key={o.optionId ?? `out${i}`} style={{ height: ROW_H, display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: 12, color: '#334155', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9', position: 'relative', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {o.label}
            <div
              title="Bağlantı çizmek için sürükleyin"
              onMouseDown={(e) => { e.stopPropagation(); startConn(e, kind === 'Start' ? null : pick(node, 'questionId', 'QuestionId'), o.optionId); }}
              style={{ position: 'absolute', right: -PORT_R, top: ROW_H / 2 - PORT_R, width: PORT_R * 2, height: PORT_R * 2, borderRadius: '50%', background: connFor(kind === 'Start' ? null : pick(node, 'questionId', 'QuestionId'), o.optionId) ? '#4F46E5' : '#cbd5e1', border: '2px solid #fff', cursor: 'crosshair' }}
            />
          </div>
        ))}
      </div>
    );
  };

  // Sağ panel — seçili düğümün çıkışları + hedef dropdown
  const selQ = selectedQid && selectedQid !== 'start' && selectedQid !== 'end' ? questionById[selectedQid] : null;
  const selOutputs = selectedQid === 'start' ? [{ optionId: null, label: 'İlk Soru' }] : (selQ ? outputsOf(selQ) : null);
  const selIsMulti = selQ && (pick(selQ, 'selectionMode', 'SelectionMode') === 'Multi' || pick(selQ, 'questionType', 'QuestionType') === 'MultiChoice');

  return (
    <div className="dps-form">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        {readOnly ? (
          <span style={{ fontSize: 12.5, fontWeight: 600, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '5px 10px' }}>Salt okunur — akış düzenlenemez</span>
        ) : (
          <>
            <span style={{ fontSize: 12, fontWeight: 600, color: saving ? '#a16207' : (dirty ? '#dc2626' : '#16a34a'), minWidth: 92 }}>
              {saving ? 'Kaydediliyor…' : (dirty ? 'Kaydedilmedi' : (savedTick > 0 ? 'Kaydedildi' : 'Güncel'))}
            </span>
            <button className="dps-btn primary" onClick={save} disabled={saving || !dirty}>Kaydet</button>
          </>
        )}
        {validation.hasCycle && <span style={{ fontSize: 12, color: '#b91c1c', background: '#fee2e2', borderRadius: 8, padding: '4px 10px' }}>⚠ Döngü var — katılımcı anketi bitiremez</span>}
        {validation.unreachable.length > 0 && <span style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '4px 10px' }}>⚠ {validation.unreachable.length} erişilemez soru</span>}
        <span style={{ fontSize: 12, color: '#64748b' }}>Çıkış noktasından (sağdaki daire) hedefe sürükleyin ya da sağ panelden seçin.</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr 240px', gap: 12, alignItems: 'start' }}>
        {/* Sol soru listesi */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Sorular</div>
          {questions.map((q) => {
            const qid = pick(q, 'id', 'Id');
            return (
              <button key={qid} type="button" onClick={() => setSelectedQid(qid)}
                style={{ textAlign: 'left', background: selectedQid === qid ? '#eef2ff' : '#fff', border: '1px solid ' + (selectedQid === qid ? '#c7d2fe' : '#e5e7eb'), borderRadius: 8, padding: '6px 9px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {pick(q, 'questionText', 'QuestionText') || '—'}
              </button>
            );
          })}
        </div>

        {/* Kanvas */}
        <div
          ref={canvasRef}
          onMouseMove={onCanvasMove}
          onMouseUp={onCanvasUp}
          onMouseLeave={() => interaction?.type === 'node' && setInteraction(null)}
          style={{ position: 'relative', overflow: 'auto', maxHeight: 600, minHeight: 480, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafafd' }}
        >
          <div style={{ position: 'relative', width: extent.w, height: extent.h, backgroundImage: 'radial-gradient(#e2e8f0 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
            <svg width={extent.w} height={extent.h} style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', zIndex: 1 }}>
              {connections.map((c) => {
                const pts = connPoints(c);
                if (!pts) return null;
                return <path key={c._k} d={bezier(pts.from, pts.to)} stroke="#4F46E5" strokeWidth="2" fill="none" markerEnd="url(#arrow)" />;
              })}
              {interaction?.type === 'conn' && (() => {
                const src = interaction.sourceQuestionId ? qNode(interaction.sourceQuestionId) : startNode;
                if (!src) return null;
                const outs = interaction.sourceQuestionId ? outputsOf(questionById[interaction.sourceQuestionId]) : [{ optionId: null }];
                const oi = Math.max(0, outs.findIndex((o) => o.optionId === interaction.sourceOptionId));
                const from = outPoint(src, oi);
                return <path d={bezier(from, pointer)} stroke="#a5b4fc" strokeWidth="2" strokeDasharray="5 4" fill="none" />;
              })()}
              <defs>
                <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L8,3 L0,6 Z" fill="#4F46E5" />
                </marker>
              </defs>
            </svg>
            {nodes.map(renderNode)}
          </div>
        </div>

        {/* Sağ panel — çıkış hedefleri */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Çıkış Hedefleri</div>
          {!selOutputs ? (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>Soldan ya da kanvastan bir düğüm seçin.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {selOutputs.map((o, i) => {
                const srcQid = selectedQid === 'start' ? null : selectedQid;
                const c = connFor(srcQid, o.optionId);
                const cur = c ? (pick(c, 'targetQuestionId', 'TargetQuestionId') ?? '__end__') : '__none__';
                return (
                  <div key={o.optionId ?? `o${i}`} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</div>
                    <select
                      value={cur}
                      onChange={(e) => {
                        const v = e.target.value;
                        setConnectionTarget(srcQid, o.optionId, v === '__none__' ? undefined : v === '__end__' ? null : v);
                      }}
                      style={{ width: '100%', padding: '5px 6px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                    >
                      {targetOptions(srcQid).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    {selIsMulti && c && (
                      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>Öncelik</span>
                        <input type="number" value={pick(c, 'priority', 'Priority') ?? 0} onChange={(e) => setPriority(c, e.target.value)}
                          style={{ width: 60, padding: '3px 5px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
